import type { MouseEvent, ReactNode } from "react";
import { numberFormatter } from "@common/helpers/number";
import type { DataGridRowAttributes } from "../features/rowKinds";
import styles from "./Grid.module.scss";

interface SeparatorRowProps {
    rowId: string;
    start: number;
    ariaRowIndex: number;
    virtualIndex: number;
    measureRef: ((element: HTMLElement | null) => void) | undefined;
    columnCount: number;
    attributes: DataGridRowAttributes | undefined;
    /** Rows the separator heads, shown after the label like a group count. */
    count: number;
    children: ReactNode;
}

// The row can't take focus, so a press would otherwise focus the scroll area,
// which treats that as a Tab in and jumps to the first row.
const preventFocus = (event: MouseEvent) => event.preventDefault();

/** A full-width label row (`separatorRows`): one cell spanning every column, never focused. */
export const SeparatorRow = ({
    rowId,
    start,
    ariaRowIndex,
    virtualIndex,
    measureRef,
    columnCount,
    attributes,
    count,
    children,
}: Readonly<SeparatorRowProps>) => (
    <div
        {...attributes}
        ref={measureRef}
        role="row"
        aria-rowindex={ariaRowIndex}
        data-index={virtualIndex}
        className={styles.row}
        style={{ transform: `translateY(${start}px)` }}
        data-row-id={rowId}
        data-separator=""
        data-measured={measureRef ? "" : undefined}
        onMouseDown={preventFocus}
    >
        <div
            role="gridcell"
            aria-colspan={columnCount}
            className={styles.separatorCell}
        >
            <span className={styles.separatorLabel}>
                {children}
                <span className={styles.groupCount}>
                    {numberFormatter.format(count)}
                </span>
            </span>
        </div>
    </div>
);
